import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import {
  Bold, Italic, List as ListIcon, ListOrdered, Heading1, Heading2,
  Quote, Code, Link as LinkIcon, Undo, Redo,
} from "lucide-react";

export default function RichTextEditor({ value, onChange, placeholder, compact = false }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({ placeholder: placeholder || "Tulis…" }),
      Link.configure({ openOnClick: true, autolink: true }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: `tiptap font-sans text-sm leading-relaxed p-3 ${compact ? "min-h-[60px]" : "min-h-[120px]"}`,
      },
    },
  });

  if (!editor) return null;

  const btn = (active) =>
    `w-8 h-8 flex items-center justify-center border border-black text-black btn-brut ${
      active ? "bg-black text-white" : "bg-white hover:bg-canary"
    }`;

  return (
    <div className="border-2 border-black bg-white">
      <div className="flex flex-wrap gap-0 border-b border-black bg-[#F9F9F9]">
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btn(editor.isActive("bold"))} title="Tebal" data-testid="rte-bold">
          <Bold size={14} strokeWidth={2.5} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(editor.isActive("italic"))} title="Miring" data-testid="rte-italic">
          <Italic size={14} strokeWidth={2.5} />
        </button>
        {!compact && (
          <>
            <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={btn(editor.isActive("heading", { level: 1 }))} title="Heading 1">
              <Heading1 size={14} strokeWidth={2.5} />
            </button>
            <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btn(editor.isActive("heading", { level: 2 }))} title="Heading 2">
              <Heading2 size={14} strokeWidth={2.5} />
            </button>
          </>
        )}
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive("bulletList"))} title="Bullet" data-testid="rte-bullet">
          <ListIcon size={14} strokeWidth={2.5} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive("orderedList"))} title="Numbered" data-testid="rte-number">
          <ListOrdered size={14} strokeWidth={2.5} />
        </button>
        {!compact && (
          <>
            <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btn(editor.isActive("blockquote"))} title="Quote">
              <Quote size={14} strokeWidth={2.5} />
            </button>
            <button type="button" onClick={() => editor.chain().focus().toggleCode().run()} className={btn(editor.isActive("code"))} title="Inline code">
              <Code size={14} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={() => {
                const url = window.prompt("URL?");
                if (url) editor.chain().focus().setLink({ href: url }).run();
              }}
              className={btn(editor.isActive("link"))}
              title="Tautan"
            >
              <LinkIcon size={14} strokeWidth={2.5} />
            </button>
          </>
        )}
        <div className="flex-1" />
        <button type="button" onClick={() => editor.chain().focus().undo().run()} className={btn(false)} title="Undo">
          <Undo size={14} strokeWidth={2.5} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()} className={btn(false)} title="Redo">
          <Redo size={14} strokeWidth={2.5} />
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
